import React, { createContext, useContext, useEffect } from "react";
import { useDebounce } from "../../hooks/useDebounce";
import useInfiniteScrollQuery from "../../hooks/useInfiniteScroll";
import WorkoutResultsService from "../../services/WorkoutResultsService";
import {
  BodypartType,
  MovementPatternType,
  UserExerciseDto,
} from "@growth-machine-llc/stridist-api-client";
import {
  ALL_OPTIONS,
  ALL_OPTIONS_TYPE,
} from "../../contexts/ExerciseLibraryFilterContext";
interface ClientExercisesFilters {
  query?: string;
  bodyparts?: BodypartType[] | ALL_OPTIONS_TYPE;
  movementPatterns?: MovementPatternType[] | ALL_OPTIONS_TYPE;
  tagIds?: number[] | ALL_OPTIONS_TYPE;
}

interface ClientExercisesContextValue {
  exercises: UserExerciseDto[];
  selectedExercise: UserExerciseDto | null;

  isLoadingInitial: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;

  filters: ClientExercisesFilters;

  setSelectedExercise: (exercise: UserExerciseDto | null) => void;
  updateFilters: (filters: Partial<ClientExercisesFilters>) => void;
  fetchNextPage: () => void;

  componentListElementRef: (node?: Element | null) => void;
}

const ClientExercisesContext =
  createContext<ClientExercisesContextValue | null>(null);

const CLIENT_EXERCISES_LIST_QUERY_KEY = "client-exercises-list";
const CLIENT_EXERCISES_PAGE_SIZE = 12;

interface ClientExercisesProviderProps {
  children: React.ReactNode;
  clientId: number;
  pageSize?: number;
}

export const ClientExercisesProvider: React.FC<
  ClientExercisesProviderProps
> = ({ children, clientId, pageSize = CLIENT_EXERCISES_PAGE_SIZE }) => {
  const [selectedExercise, setSelectedExercise] =
    React.useState<UserExerciseDto | null>(null);
  const [filters, setFilters] = React.useState<ClientExercisesFilters>({
    query: "",
    bodyparts: ALL_OPTIONS,
    movementPatterns: ALL_OPTIONS,
    tagIds: ALL_OPTIONS,
  });
  const [autoSelectInitial, setAutoSelectInitial] = React.useState(true);

  const delayedQuery = useDebounce(filters.query || "", 250);

  const actualBodyparts =
    filters.bodyparts === ALL_OPTIONS ? null : filters.bodyparts;
  const actualMovementPatterns =
    filters.movementPatterns === ALL_OPTIONS ? null : filters.movementPatterns;
  const actualTagIds = filters.tagIds === ALL_OPTIONS ? null : filters.tagIds;

  const {
    data: exercisesData,
    ref: componentListElementRef,
    isLoading: isLoadingInitial,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteScrollQuery({
    queryKey: [
      CLIENT_EXERCISES_LIST_QUERY_KEY,
      {
        query: delayedQuery,
        movementPatterns: actualMovementPatterns,
        bodyparts: actualBodyparts,
        tagIds: actualTagIds,
        clientId,
      },
    ],
    queryFn: ({ pageParam = 1 }) =>
      WorkoutResultsService.getUserExercises(
        pageParam as number,
        pageSize,
        clientId,
        delayedQuery,
        actualBodyparts,
        actualMovementPatterns,
        actualTagIds,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      lastPage?.hasNextPage ? pages.length + 1 : undefined,
  });

  const exercises = exercisesData?.pages.flatMap((page) => page.items) ?? [];

  useEffect(() => {
    if (autoSelectInitial && exercises.length > 0 && !selectedExercise) {
      setSelectedExercise(exercises[0]);
      setAutoSelectInitial(false);
    }
  }, [exercises, selectedExercise, autoSelectInitial]);

  const updateFilters = React.useCallback(
    (newFilters: Partial<ClientExercisesFilters>) => {
      setFilters((prev) => ({ ...prev, ...newFilters }));
    },
    [],
  );

  const contextValue: ClientExercisesContextValue = {
    exercises,
    selectedExercise,
    isLoadingInitial,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    filters,
    setSelectedExercise,
    updateFilters,
    fetchNextPage,
    componentListElementRef: (node?: Element | null) =>
      componentListElementRef(node as HTMLDivElement | null),
  };

  return (
    <ClientExercisesContext.Provider value={contextValue}>
      {children}
    </ClientExercisesContext.Provider>
  );
};

export const useClientExercises = (): ClientExercisesContextValue => {
  const context = useContext(ClientExercisesContext);
  if (!context) {
    throw new Error(
      "useClientExercises must be used within ClientExercisesProvider",
    );
  }
  return context;
};

export default ClientExercisesContext;
